// src/pages/Search.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import SearchableDropdown from '../components/SearchableDropdown';
import ApiService from '../services/apiService';

const Search = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [isJournalOpen, setIsJournalOpen] = useState(false);
  const [courtSearchTerm, setCourtSearchTerm] = useState('');

  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const [isPageOpen, setIsPageOpen] = useState(false);
  const [courts, setCourts] = useState([]);
  const [isLoadingCourts, setIsLoadingCourts] = useState(true);
  const [courtsError, setCourtsError] = useState('');
  const [selectedCourts, setSelectedCourts] = useState({});

  // API data states
  const [journals, setJournals] = useState([]);
  const [years, setYears] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [pages, setPages] = useState([]);

  // Loading states
  const [isLoadingJournals, setIsLoadingJournals] = useState(true);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [isLoadingVolumes, setIsLoadingVolumes] = useState(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);

  // Error states
  const [journalsError, setJournalsError] = useState('');
  const [yearsError, setYearsError] = useState('');
  const [volumesError, setVolumesError] = useState('');
  const [pagesError, setPagesError] = useState('');

  const API_BASE_URL = 'https://api.ljsoftonline.com';

  // Form data for all search fields
  const [formData, setFormData] = useState({
    selectedJournal: 'Select a option',
    selectedYear: 'Select a option',
    selectedVolume: 'Select a option',
    selectedPage: 'Select a option',
    appellant: '',
    respondent: '',
    caseNo: '',
    judges: '',
    searchJudgeType: 'exact',
    searchType: 'all',
    advocates: '',
    queryText: '',
    nearWords: '5',
    stateFavourCases: false,
    searchOrder: 'most-relevant',
    headnote: true,
    fulltext: true,
    bench: 'All',
    acts: '',
    section: '',
    dateWiseSearch: false,
    dateFrom: '01-01-1950',
    dateTo: '24-09-2025'
  });

  // Keep all your existing useEffect, API methods, and handlers here...
  // [Copy all the makeAuthenticatedRequest, loadCourts, loadJournals, etc. functions from your original code]

  const makeAuthenticatedRequest = async (url) => {
    const token = ApiService.getAccessToken();
    if (!token) throw new Error('No access token found. Please login again.');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (response.status === 401) {
      ApiService.clearTokensAndRedirect();
      throw new Error('Session expired. Please login again.');
    }
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  };

  useEffect(() => {
    const storedKeyword = localStorage.getItem('SearchHAdvance');
    if (storedKeyword) {
      setFormData(prev => ({ ...prev, queryText: storedKeyword }));
    }
    loadCourts();
    loadJournals();
  }, []);

  const loadCourts = async () => {
    try {
      setIsLoadingCourts(true);
      setCourtsError('');
      const permissionsData = await ApiService.getUserPermissions();
      const courtsData = permissionsData.courts || [];
      setCourts(courtsData);
      const initialSelection = {};
      courtsData.forEach(court => { initialSelection[court.key] = true; });
      setSelectedCourts(initialSelection);
    } catch (error) {
      console.error('Error loading courts:', error);
      setCourtsError(error.message || 'Failed to load courts');
    } finally {
      setIsLoadingCourts(false);
    }
  };

  const loadJournals = async () => {
    try {
      setIsLoadingJournals(true);
      setJournalsError('');
      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals`);
      setJournals(data);
    } catch (error) {
      console.error('Error loading journals:', error);
      setJournalsError(error.message);
    } finally {
      setIsLoadingJournals(false);
    }
  };

  const loadYears = async (journalName) => {
    try {
      setIsLoadingYears(true);
      setYearsError('');
      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years`);
      setYears(data);
    } catch (error) {
      console.error('Error loading years:', error);
      setYearsError(error.message);
      setYears([]);
    } finally {
      setIsLoadingYears(false);
    }
  };

  const loadVolumes = async (journalName, year) => {
    try {
      setIsLoadingVolumes(true);
      setVolumesError('');
      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years/${year}/volumes`);
      setVolumes(data);
    } catch (error) {
      console.error('Error loading volumes:', error);
      setVolumesError(error.message);
      setVolumes([]);
    } finally {
      setIsLoadingVolumes(false);
    }
  };

  const loadPages = async (journalName, year, volume) => {
    try {
      setIsLoadingPages(true);
      setPagesError('');
      const data = await makeAuthenticatedRequest(`${API_BASE_URL}/Journals/${encodeURIComponent(journalName)}/years/${year}/volumes/${encodeURIComponent(volume)}/pages`);
      setPages(data);
    } catch (error) {
      console.error('Error loading pages:', error);
      setPagesError(error.message);
      setPages([]);
    } finally {
      setIsLoadingPages(false);
    }
  };

  const handleJournalSelect = (journal) => {
    setFormData(prev => ({
      ...prev,
      selectedJournal: journal,
      selectedYear: 'Select a option',
      selectedVolume: 'Select a option',
      selectedPage: 'Select a option'
    }));
    setIsJournalOpen(false);
    setYears([]);
    setVolumes([]);
    setPages([]);
    if (journal !== 'Select a option') loadYears(journal);
  };

  const handleYearSelect = (year) => {
    setFormData(prev => ({
      ...prev,
      selectedYear: year,
      selectedVolume: 'Select a option',
      selectedPage: 'Select a option'
    }));
    setIsYearOpen(false);
    setVolumes([]);
    setPages([]);
    if (year !== 'Select a option' && formData.selectedJournal !== 'Select a option') {
      loadVolumes(formData.selectedJournal, year);
    }
  };

  const handleVolumeSelect = (volume) => {
    setFormData(prev => ({
      ...prev,
      selectedVolume: volume,
      selectedPage: 'Select a option'
    }));
    setIsVolumeOpen(false);
    setPages([]);
    if (volume !== 'Select a option' && formData.selectedJournal !== 'Select a option' && formData.selectedYear !== 'Select a option') {
      loadPages(formData.selectedJournal, formData.selectedYear, volume);
    }
  };

  const handlePageSelect = (page) => {
    setFormData(prev => ({ ...prev, selectedPage: page }));
    setIsPageOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const handleCourtToggle = (courtKey) => {
    setSelectedCourts(prev => ({ ...prev, [courtKey]: !prev[courtKey] }));
  };

  const handleSelectAllCourts = () => {
    const allSelected = {};
    courts.forEach(court => { allSelected[court.key] = true; });
    setSelectedCourts(allSelected);
  };

  const handleUnselectAllCourts = () => {
    const allUnselected = {};
    courts.forEach(court => { allUnselected[court.key] = false; });
    setSelectedCourts(allUnselected);
  };

  const handleSearch = async (searchType) => {
    setIsLoading(true);
    setError('');

    try {
      const journal = formData.selectedJournal !== 'Select a option' ? formData.selectedJournal : null;
      const year = formData.selectedYear !== 'Select a option' ? formData.selectedYear : null;
      const volume = formData.selectedVolume !== 'Select a option' && formData.selectedVolume !== "0" ? formData.selectedVolume : null;
      const page = formData.selectedPage !== 'Select a option' ? formData.selectedPage : null;
      const searchIn = 'B';
      localStorage.setItem('SearchHKeyword', formData.queryText);
      
      let ActSearch = formData.acts;
      if (formData.acts && formData.section) {
        ActSearch = `${formData.acts || ''} ${journal || ''} -- ${formData.section || ''}`.trim();
      } else if (formData.section) {
        ActSearch = formData.section;
      }

      const trueKeys = selectedCourts.ALL ? ["ALL"] : Object.keys(selectedCourts).filter(key => selectedCourts[key]);
      
      const payload = {
        requests: [{
          query: formData.queryText,
          type: formData.searchType,
          querySlop: formData.nearWords,
          searchIn: searchIn,
          mainkeys: searchType == 'all' ? trueKeys : [searchType.toUpperCase()],
          citation: `${year || ''} ${volume || ''} ${journal || ''} ${page || ''}`.trim(),
          appellant: formData.appellant || '',
          respondent: formData.respondent || '',
          caseNo: formData.caseNo,
          judges: formData.judges,
          advocate: formData.advocates,
          act: ActSearch
        }],
        sortBy: formData.searchOrder === 'most-relevant' ? 'rele' :
          formData.searchOrder === 'most-recent' ? 'year' :
            formData.searchOrder === 'most-referred' ? 'rele' : 'year',
        sortOrder: formData.searchOrder === 'oldest' ? 'asc' : 'desc',
        page: 1,
        pageSize: 25,
        inst: '',
        prompt: "Advance",
      };

      const apiResponse = await ApiService.executeAllSearch(payload);
      const searchResults = apiResponse.hits || [];
      const totalCount = apiResponse.total || 0;

      if (searchResults.length === 0) {
        setError('No results found for your search query. Try different keywords.');
        setIsLoading(false);
        return;
      }

      const resultsData = {
        results: apiResponse.hits || [],
        totalCount: apiResponse.total || 0,
        query: formData.queryText,
        searchType: 'Advance Search',
        timestamp: new Date().toISOString(),
        courtsList: apiResponse.courtsList || [],
        yearList: apiResponse.yearList || [],
        searchData: {
          query: formData.queryText,
          searchType: formData.searchType,
          sortOrder: formData.searchOrder,
          searchIn,
          querySlop: formData.nearWords,
          citation: `${year || ''} ${volume || ''} ${journal || ''} ${page || ''}`.trim(),
          mainkeys: searchType == 'all' ? trueKeys : [searchType.toUpperCase()],
          appellant: formData.appellant || '',
          respondent: formData.respondent || '',
          caseNo: formData.caseNo,
          judges: formData.caseNo,
          advocate: formData.advocates,
          act: ActSearch
        }
      };

      localStorage.setItem('searchResults', JSON.stringify(resultsData));
      localStorage.setItem('SearchHAdvance', formData.queryText);
      navigate('/results');

    } catch (error) {
      console.error('Advanced Search failed:', error);
      setError(error.message || 'Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      selectedJournal: 'Select a option',
      selectedYear: 'Select a option',
      selectedVolume: 'Select a option',
      selectedPage: 'Select a option',
      appellant: '',
      respondent: '',
      caseNo: '',
      judges: '',
      searchJudgeType: 'exact',
      searchType: 'all',
      advocates: '',
      queryText: '',
      nearWords: '5',
      stateFavourCases: false,
      searchOrder: 'most-relevant',
      headnote: true,
      fulltext: true,
      bench: 'All',
      acts: '',
      section: '',
      dateWiseSearch: false,
      dateFrom: '01-01-1950',
      dateTo: '24-09-2025'
    });
    localStorage.setItem('SearchHAdvance', '');
  };

  const handleResetDate = () => {
    setFormData(prev => ({
      ...prev,
      dateFrom: '01-01-1950',
      dateTo: '24-09-2025'
    }));
  };

  return (
    <div className="search-layout">
      <Navbar />
      <SearchNavigation />

      <div className="search-main-content">
        {/* Hero Section */}
        <div className="search-hero">
          <div className="hero-badge">
            <i className="bx bx-filter-alt"></i>
            <span>Advanced Search</span>
          </div>
          <h1 className="hero-title">
            Comprehensive Legal Search
          </h1>
          <p className="hero-subtitle">
            Powerful multi-criteria search with advanced filtering options
          </p>
        </div>

        <div className="search-container">
          {/* Error Alert */}
          {error && (
            <div className="error-alert">
              <i className="bx bx-error-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Main Grid - 2 Columns */}
          <div className="search-grid">
            {/* LEFT COLUMN */}
            <div className="search-column">
              {/* Citations Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-book-bookmark"></i>
                  </div>
                  <h3 className="card-title-modern">Citations</h3>
                </div>
               <div className="card-body-modern">
                  <div className="form-grid-2">
                    {/* Journal Dropdown */}
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-book-open"></i>
                        Journal
                      </label>
                      <div className="dropdown-wrapper-modern">
                        <button
                          className={`dropdown-btn-modern ${formData.selectedJournal !== 'Select a option' ? 'selected' : ''}`}
                          onClick={() => setIsJournalOpen(!isJournalOpen)}
                          disabled={isLoadingJournals}
                        >
                          <span className="dropdown-text">
                            {isLoadingJournals ? (
                              <>
                                <i className="bx bx-loader-alt bx-spin"></i>
                                Loading...
                              </>
                            ) : formData.selectedJournal}
                          </span>
                          <i className={`bx bx-chevron-down dropdown-arrow ${isJournalOpen ? 'open' : ''}`}></i>
                        </button>

                        {(isJournalOpen || isLoadingJournals || journalsError) && (
                          <>
                            <div className="dropdown-backdrop" onClick={() => setIsJournalOpen(false)} />
                            <SearchableDropdown
                              items={journals}
                              selectedItem={formData.selectedJournal}
                              onSelect={handleJournalSelect}
                              isOpen={isJournalOpen}
                              onToggle={setIsJournalOpen}
                              isLoading={isLoadingJournals}
                              error={journalsError}
                              loadingText="Loading journals..."
                              placeholder="journals"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Year Dropdown */}
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-calendar"></i>
                        Year
                      </label>
                      <div className="dropdown-wrapper-modern">
                        <button
                          className={`dropdown-btn-modern ${formData.selectedYear !== 'Select a option' ? 'selected' : ''}`}
                          onClick={() => setIsYearOpen(!isYearOpen)}
                          disabled={isLoadingYears || formData.selectedJournal === 'Select a option'}
                        >
                          <span className="dropdown-text">
                            {isLoadingYears ? (
                              <>
                                <i className="bx bx-loader-alt bx-spin"></i>
                                Loading...
                              </>
                            ) : formData.selectedYear}
                          </span>
                          <i className={`bx bx-chevron-down dropdown-arrow ${isYearOpen ? 'open' : ''}`}></i>
                        </button>

                        {(isYearOpen || isLoadingYears || yearsError) && formData.selectedJournal !== 'Select a option' && (
                          <>
                            <div className="dropdown-backdrop" onClick={() => setIsYearOpen(false)} />
                            <SearchableDropdown
                              items={years}
                              selectedItem={formData.selectedYear}
                              onSelect={handleYearSelect}
                              isOpen={isYearOpen}
                              onToggle={setIsYearOpen}
                              isLoading={isLoadingYears}
                              error={yearsError}
                              loadingText="Loading years..."
                              placeholder="years"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Volume Dropdown */}
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-book"></i>
                        Volume
                      </label>
                      <div className="dropdown-wrapper-modern">
                        <button
                          className={`dropdown-btn-modern ${formData.selectedVolume !== 'Select a option' ? 'selected' : ''}`}
                          onClick={() => setIsVolumeOpen(!isVolumeOpen)}
                          disabled={isLoadingVolumes || formData.selectedYear === 'Select a option'}
                        >
                          <span className="dropdown-text">
                            {isLoadingVolumes ? (
                              <>
                                <i className="bx bx-loader-alt bx-spin"></i>
                                Loading...
                              </>
                            ) : formData.selectedVolume}
                          </span>
                          <i className={`bx bx-chevron-down dropdown-arrow ${isVolumeOpen ? 'open' : ''}`}></i>
                        </button>

                        {(isVolumeOpen || isLoadingVolumes || volumesError) && formData.selectedYear !== 'Select a option' && (
                          <>
                            <div className="dropdown-backdrop" onClick={() => setIsVolumeOpen(false)} />
                            <SearchableDropdown
                              items={volumes}
                              selectedItem={formData.selectedVolume}
                              onSelect={handleVolumeSelect}
                              isOpen={isVolumeOpen}
                              onToggle={setIsVolumeOpen}
                              isLoading={isLoadingVolumes}
                              error={volumesError}
                              loadingText="Loading volumes..."
                              placeholder="volumes"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    {/* Page Dropdown */}
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-file"></i>
                        Page
                      </label>
                      <div className="dropdown-wrapper-modern">
                        <button
                          className={`dropdown-btn-modern ${formData.selectedPage !== 'Select a option' ? 'selected' : ''}`}
                          onClick={() => setIsPageOpen(!isPageOpen)}
                          disabled={isLoadingPages || formData.selectedVolume === 'Select a option'}
                        >
                          <span className="dropdown-text">
                            {isLoadingPages ? (
                              <>
                                <i className="bx bx-loader-alt bx-spin"></i>
                                Loading...
                              </>
                            ) : formData.selectedPage}
                          </span>
                          <i className={`bx bx-chevron-down dropdown-arrow ${isPageOpen ? 'open' : ''}`}></i>
                        </button>

                        {(isPageOpen || isLoadingPages || pagesError) && formData.selectedVolume !== 'Select a option' && (
                          <>
                            <div className="dropdown-backdrop" onClick={() => setIsPageOpen(false)} />
                            <SearchableDropdown
                              items={pages}
                              selectedItem={formData.selectedPage}
                              onSelect={handlePageSelect}
                              isOpen={isPageOpen}
                              onToggle={setIsPageOpen}
                              isLoading={isLoadingPages}
                              error={pagesError}
                              loadingText="Loading pages..."
                              placeholder="pages"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Party Names Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-user-circle"></i>
                  </div>
                  <h3 className="card-title-modern">Party Names</h3>
                </div>
                <div className="card-body-modern">
                  <div className="form-field-modern">
                    <label className="field-label-modern">
                      <i className="bx bx-user"></i>
                      Appellant
                    </label>
                    <input
                      type="text"
                      className="input-modern"
                      placeholder="Enter appellant name"
                      value={formData.appellant}
                      onChange={(e) => handleInputChange('appellant', e.target.value)}
                    />
                  </div>
                  <div className="form-field-modern">
                    <label className="field-label-modern">
                      <i className="bx bx-user"></i>
                      Respondent
                    </label>
                    <input
                      type="text"
                      className="input-modern"
                      placeholder="Enter respondent name"
                      value={formData.respondent}
                      onChange={(e) => handleInputChange('respondent', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Case Number Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-hash"></i>
                  </div>
                  <h3 className="card-title-modern">Case Number</h3>
                </div>
                <div className="card-body-modern">
                  <div className="form-field-modern">
                    <label className="field-label-modern">
                      <i className="bx bx-file-blank"></i>
                      Case No
                    </label>
                    <input
                      type="text"
                      className="input-modern"
                      placeholder="Enter case number"
                      value={formData.caseNo}
                      onChange={(e) => handleInputChange('caseNo', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Judge Name Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-briefcase"></i>
                  </div>
                  <h3 className="card-title-modern">Judge Name</h3>
                </div>
                <div className="card-body-modern">
                  <div className="form-field-modern">
                    <label className="field-label-modern">
                      <i className="bx bx-user-pin"></i>
                      Judge Name
                    </label>
                    <input
                      type="text"
                      className="input-modern"
                      placeholder="Enter judge name"
                      value={formData.judges}
                      onChange={(e) => handleInputChange('judges', e.target.value)}
                    />
                  </div>
                  <div className="radio-group-modern">
                    <label className="radio-modern">
                      <input
                        type="radio"
                        name="judgeSearch"
                        value="exact"
                        checked={formData.searchJudgeType === 'exact'}
                        onChange={(e) => handleInputChange('searchJudgeType', e.target.value)}
                      />
                      <span className="radio-mark-modern"></span>
                      <span className="radio-label-modern">Exact Match</span>
                    </label>
                    <label className="radio-modern">
                      <input
                        type="radio"
                        name="judgeSearch"
                        value="free"
                        checked={formData.searchJudgeType === 'free'}
                        onChange={(e) => handleInputChange('searchJudgeType', e.target.value)}
                      />
                      <span className="radio-mark-modern"></span>
                      <span className="radio-label-modern">Free Search</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Advocate Name Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-briefcase-alt"></i>
                  </div>
                  <h3 className="card-title-modern">Advocate Name</h3>
                </div>
                <div className="card-body-modern">
                  <div className="form-field-modern">
                    <label className="field-label-modern">
                      <i className="bx bx-user-check"></i>
                      Advocate Name
                    </label>
                    <input
                      type="text"
                      className="input-modern"
                      placeholder="Enter advocate name"
                      value={formData.advocates}
                      onChange={(e) => handleInputChange('advocates', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="search-column">
              {/* Subject and Topic Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-search-alt-2"></i>
                  </div>
                  <h3 className="card-title-modern">Subject & Topic</h3>
                </div>
                <div className="card-body-modern">
                  {/* Search Type Options */}
                  <div className="search-type-cards-mini">
                    <button
                      type="button"
                      className={`type-card-mini ${formData.searchType === 'exact' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchType', 'exact')}
                    >
                      <i className="bx bx-quote-alt-left"></i>
                      <span>Exact Phrase</span>
                    </button>
                    <button
                      type="button"
                      className={`type-card-mini ${formData.searchType === 'all' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchType', 'all')}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>All Words</span>
                    </button>
                    <button
                      type="button"
                      className={`type-card-mini ${formData.searchType === 'near' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchType', 'near')}
                    >
                      <i className="bx bx-target-lock"></i>
                      <span>Near</span>
                      <input
                        type="text"
                        className="near-input-mini"
                        placeholder="5"
                        value={formData.nearWords || '5'}
                        onChange={(e) => handleInputChange('nearWords', e.target.value)}
                        disabled={formData.searchType !== 'near'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInputChange('searchType', 'near');
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      className={`type-card-mini ${formData.searchType === 'magic' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchType', 'magic')}
                    >
                      <i className="bx bx-magic-wand"></i>
                      <span>Magic</span>
                    </button>
                  </div>

                  {/* Query Text Area */}
                  <div className="form-field-modern">
                    <textarea
                      className="textarea-modern"
                      rows="3"
                      placeholder="Enter your search query here..."
                      value={formData.queryText}
                      onChange={(e) => handleInputChange('queryText', e.target.value)}
                    />
                  </div>

                  {/* Sort Options */}
                  <div className="filter-chips-row">
                    <button
                      type="button"
                      className={`chip-modern ${formData.searchOrder === 'most-relevant' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchOrder', 'most-relevant')}
                    >
                      <i className="bx bx-star"></i>
                      Most Relevant
                    </button>
                    <button
                      type="button"
                      className={`chip-modern ${formData.searchOrder === 'most-recent' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchOrder', 'most-recent')}
                    >
                      <i className="bx bx-time"></i>
                      Most Recent
                    </button>
                    <button
                      type="button"
                      className={`chip-modern ${formData.searchOrder === 'most-referred' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchOrder', 'most-referred')}
                    >
                      <i className="bx bx-trending-up"></i>
                      Most Referred
                    </button>
                    <button
                      type="button"
                      className={`chip-modern ${formData.searchOrder === 'oldest' ? 'active' : ''}`}
                      onClick={() => handleInputChange('searchOrder', 'oldest')}
                    >
                      <i className="bx bx-history"></i>
                      Oldest
                    </button>
                  </div>

                  {/* Content Type Toggles */}
                  <div className="toggle-group-modern">
                    <label className="toggle-modern">
                      <input
                        type="checkbox"
                        checked={formData.headnote}
                        onChange={(e) => handleCheckboxChange('headnote', e.target.checked)}
                      />
                      <span className="toggle-mark-modern"></span>
                      <span className="toggle-label-modern">Head Notes</span>
                    </label>
                    <label className="toggle-modern">
                      <input
                        type="checkbox"
                        checked={formData.fulltext}
                        onChange={(e) => handleCheckboxChange('fulltext', e.target.checked)}
                      />
                      <span className="toggle-mark-modern"></span>
                      <span className="toggle-label-modern">Full Text</span>
                    </label>
                  </div>

                  {/* Courts Selection */}
                  <div className="courts-actions-container">
                    <div className="courts-section-modern">
                      <div className="courts-header-modern">
                        <input
                          type="text"
                          className="court-search-modern"
                          placeholder="Search courts..."
                          value={courtSearchTerm}
                          onChange={(e) => setCourtSearchTerm(e.target.value)}
                        />
                        <button
                          className="btn-unselect-modern"
                          onClick={handleUnselectAllCourts}
                          disabled={isLoadingCourts}
                        >
                          Unselect All
                        </button>
                      </div>

                      {isLoadingCourts ? (
                        <div className="loading-state-modern">
                          <i className="bx bx-loader-alt bx-spin"></i>
                          <p>Loading courts...</p>
                        </div>
                      ) : courtsError ? (
                        <div className="error-state-modern">
                          <p>{courtsError}</p>
                          <button onClick={loadCourts}>Retry</button>
                        </div>
                      ) : (
                        <div className="courts-list-modern">
                          {courts
                            .filter(court =>
                              court.value.toLowerCase().includes(courtSearchTerm.toLowerCase())
                            )
                            .map((court) => (
                              <label key={court.key} className="court-item-modern">
                                <input
                                  type="checkbox"
                                  checked={selectedCourts[court.key] || false}
                                  onChange={(e) => handleCourtToggle(court.key)}
                                />
                                <span className="court-checkbox-modern"></span>
                                <span className="court-name-modern">{court.value}</span>
                              </label>
                            ))}
                          {courts.filter(court =>
                            court.value.toLowerCase().includes(courtSearchTerm.toLowerCase())
                          ).length === 0 && (
                              <div className="no-courts-modern">
                                No courts found matching "{courtSearchTerm}"
                              </div>
                            )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons-modern">
                      <button
                        className="action-btn-modern search-sc-modern"
                        onClick={() => handleSearch('sc')}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <i className="bx bx-loader-alt bx-spin"></i>
                        ) : (
                          <i className="bx bx-search"></i>
                        )}
                        Search SC
                      </button>
                      <button
                        className="action-btn-modern search-all-modern"
                        onClick={() => handleSearch('all')}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <i className="bx bx-loader-alt bx-spin"></i>
                        ) : (
                          <i className="bx bx-search"></i>
                        )}
                        Search All
                      </button>
                      <button
                        className="action-btn-modern clear-modern"
                        onClick={handleClear}
                        type="button"
                      >
                        <i className="bx bx-refresh"></i>
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Acts and Sections Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <div className="header-icon-wrapper">
                    <i className="bx bx-book-content"></i>
                  </div>
                  <h3 className="card-title-modern">Acts & Sections</h3>
                </div>
                <div className="card-body-modern">
                  <div className="form-grid-2">
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-book"></i>
                        Acts
                      </label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="Enter act name"
                        value={formData.acts}
                        onChange={(e) => handleInputChange('acts', e.target.value)}
                      />
                    </div>
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-list-ol"></i>
                        Section
                      </label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="Enter section"
                        value={formData.section}
                        onChange={(e) => handleInputChange('section', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Wise Search Card */}
              <div className="search-card">
                <div className="card-header-modern">
                  <label className="checkbox-header-modern">
                    <input
                      type="checkbox"
                      checked={formData.dateWiseSearch}
                      onChange={(e) => handleCheckboxChange('dateWiseSearch', e.target.checked)}
                    />
                    <span className="checkbox-mark-header"></span>
                    <div className="header-icon-wrapper">
                      <i className="bx bx-calendar-event"></i>
                    </div>
                    <h3 className="card-title-modern">Date Wise Search</h3>
                  </label>
                </div>
                <div className="card-body-modern">
                  <div className="form-grid-2">
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-calendar"></i>
                        From
                      </label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="DD-MM-YYYY"
                        value={formData.dateFrom}
                        onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                        disabled={!formData.dateWiseSearch}
                      />
                    </div>
                    <div className="form-field-modern">
                      <label className="field-label-modern">
                        <i className="bx bx-calendar"></i>
                        To
                      </label>
                      <input
                        type="text"
                        className="input-modern"
                        placeholder="DD-MM-YYYY"
                        value={formData.dateTo}
                        onChange={(e) => handleInputChange('dateTo', e.target.value)}
                        disabled={!formData.dateWiseSearch}
                      />
                    </div>
                  </div>
                  <button
                    className="reset-date-modern"
                    onClick={handleResetDate}
                    type="button"
                    disabled={!formData.dateWiseSearch}
                  >
                    <i className="bx bx-refresh"></i>
                    Reset Date
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .search-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .search-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .search-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .hero-badge i {
          font-size: 16px;
        }

        .hero-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           SEARCH CONTAINER
        ============================================ */
        .search-container {
    max-width: 1400px;
    margin: -30px auto 0; /* Reduced bottom margin from 60px */
    padding: 0 20px 30px; /* Changed from 60px to 30px bottom padding */
    position: relative;
    z-index: 10;
  }

        /* Error Alert */
        .error-alert {
          background: linear-gradient(135deg, #fee, #fdd);
          border: 1px solid #fcc;
          color: #c33;
          padding: 14px 20px;
          border-radius: 12px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.15);
        }

        .error-alert i {
          font-size: 20px;
          flex-shrink: 0;
        }

        /* ============================================
           SEARCH GRID - 2 COLUMNS
        ============================================ */
        .search-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px; /* Reduced from 20px to make it more compact */
  }

  .search-column {
    display: flex;
    flex-direction: column;
    gap: 16px; /* Reduced from 20px */
  }

        /* ============================================
           SEARCH CARDS
        ============================================ */
      .search-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
    margin-bottom: 0; /* Remove any default margin */
  }

  


        .search-card:hover {
          box-shadow: 0 8px 30px rgba(245, 166, 35, 0.12);
        }

        /* Card Header */
        .card-header-modern {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 2px solid #f5f5f5;
        }

        .header-icon-wrapper {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          flex-shrink: 0;
        }

        .card-title-modern {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin: 0;
        }

        /* Checkbox Header */
        .checkbox-header-modern {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          user-select: none;
          width: 100%;
        }

        .checkbox-header-modern input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .checkbox-mark-header {
          width: 20px;
          height: 20px;
          border: 2px solid #E91E63;
          border-radius: 6px;
          background: white;
          transition: all 0.2s ease;
          position: relative;
          flex-shrink: 0;
        }

        .checkbox-header-modern input[type="checkbox"]:checked ~ .checkbox-mark-header {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-color: transparent;
        }

        .checkbox-header-modern input[type="checkbox"]:checked ~ .checkbox-mark-header::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 2px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        /* Card Body */
        .card-body-modern {
          padding: 16px;
        }

        /* ============================================
           FORM ELEMENTS
        ============================================ */
        .form-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .form-field-modern {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-label-modern {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 700;
          color: #555;
        }

        .field-label-modern i {
          font-size: 16px;
          color: #E91E63;
        }

        /* Input Fields */
        .input-modern,
        .textarea-modern {
          width: 100%;
          padding: 12px 14px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 14px;
          color: #333;
          background: white;
          transition: all 0.3s ease;
        }

        .input-modern:focus,
        .textarea-modern:focus {
          outline: none;
          border-color: #E91E63;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.1);
        }

        .input-modern:disabled,
        .textarea-modern:disabled {
          background: #f5f5f5;
          color: #999;
          cursor: not-allowed;
        }

        .textarea-modern {
          resize: vertical;
          min-height: 80px;
          font-family: inherit;
        }

        /* ============================================
           DROPDOWN MODERN
        ============================================ */
        .dropdown-wrapper-modern {
          position: relative;
        }

        .dropdown-btn-modern {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .dropdown-btn-modern:hover:not(:disabled) {
          border-color: #E91E63;
        }

        .dropdown-btn-modern:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: #f5f5f5;
        }

        .dropdown-btn-modern.selected {
          border-color: #c41849;
          color: #333;
          font-weight: 600;
        }

        .dropdown-text {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dropdown-arrow {
          font-size: 20px;
          color: #999;
          transition: transform 0.3s ease;
        }

        .dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .dropdown-backdrop {
          position: fixed;
          inset: 0;
          z-index: 999;
        }

        /* ============================================
           SEARCH TYPE CARDS MINI
        ============================================ */
        .search-type-cards-mini {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          margin-bottom: 16px;
        }

        .type-card-mini {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 8px;
          background: #fafafa;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 12px;
          font-weight: 600;
          color: #666;
        }

        .type-card-mini i {
          font-size: 20px;
          color: #E91E63;
        }

        .type-card-mini:hover {
          border-color: #E91E63;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 166, 35, 0.15);
        }

        .type-card-mini.active {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-color: transparent;
          color: white;
          box-shadow: 0 4px 16px rgba(233, 30, 99, 0.3);
        }

        .type-card-mini.active i {
          color: white;
        }

        .near-input-mini {
          width: 40px;
          padding: 4px 6px;
          border: 1px solid #ddd;
          border-radius: 6px;
          text-align: center;
          font-size: 12px;
          font-weight: 700;
          margin-top: 4px;
        }

        .near-input-mini:focus {
          outline: none;
          border-color: #E91E63;
        }

        .near-input-mini:disabled {
          background: #f5f5f5;
          color: #999;
        }

        /* ============================================
           FILTER CHIPS
        ============================================ */
        .filter-chips-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }

        .chip-modern {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #fafafa;
          border: 2px solid #e0e0e0;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          color: #555;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .chip-modern i {
          font-size: 14px;
        }

        .chip-modern:hover {
          border-color: #E91E63;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.15);
        }

        .chip-modern.active {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-color: transparent;
          color: white;
          box-shadow: 0 2px 10px rgba(233, 30, 99, 0.3);
        }

        /* ============================================
           RADIO AND TOGGLE GROUPS
        ============================================ */
        .radio-group-modern {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .radio-modern {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }

        .radio-modern input[type="radio"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .radio-mark-modern {
          width: 18px;
          height: 18px;
          border: 2px solid #ddd;
          border-radius: 50%;
          background: white;
          transition: all 0.2s ease;
          position: relative;
          flex-shrink: 0;
        }

        .radio-modern:hover .radio-mark-modern {
          border-color: #E91E63;
        }

        .radio-modern input[type="radio"]:checked ~ .radio-mark-modern {
          border-color: #E91E63;
          background: linear-gradient(135deg, #E91E63, #c41849);
        }

        .radio-modern input[type="radio"]:checked ~ .radio-mark-modern::after {
          content: '';
          position: absolute;
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          top: 3px;
          left: 3px;
        }

        .radio-label-modern {
          font-size: 14px;
          font-weight: 600;
          color: #555;
        }

        /* Toggle Group */
        .toggle-group-modern {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .toggle-modern {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }

        .toggle-modern input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .toggle-mark-modern {
          width: 18px;
          height: 18px;
          border: 2px solid #ddd;
          border-radius: 6px;
          background: white;
          transition: all 0.2s ease;
          position: relative;
          flex-shrink: 0;
        }

        .toggle-modern:hover .toggle-mark-modern {
          border-color: #E91E63;
        }

        .toggle-modern input[type="checkbox"]:checked ~ .toggle-mark-modern {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-color: transparent;
        }

        .toggle-modern input[type="checkbox"]:checked ~ .toggle-mark-modern::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 2px;
          width: 3px;
          height: 7px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .toggle-label-modern {
          font-size: 14px;
          font-weight: 600;
          color: #555;
        }

        /* ============================================
           COURTS SECTION
        ============================================ */
        .courts-actions-container {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        .courts-section-modern {
          flex: 1;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          overflow: hidden;
        }

        .courts-header-modern {
          padding: 12px;
          background: #fafafa;
          border-bottom: 1px solid #e0e0e0;
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .court-search-modern {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 13px;
        }

        .court-search-modern:focus {
          outline: none;
          border-color: #E91E63;
        }

        .btn-unselect-modern {
          padding: 8px 12px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s ease;
        }

        .btn-unselect-modern:hover:not(:disabled) {
          background: #5a6268;
        }

        .btn-unselect-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .courts-list-modern {
          max-height: 150px;
          overflow-y: auto;
          padding: 8px;
          background: white;
        }

        .court-item-modern {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          user-select: none;
        }

        .court-item-modern:hover {
          background: #f8f9fa;
        }

        .court-item-modern input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .court-checkbox-modern {
          width: 16px;
          height: 16px;
          border: 2px solid #ddd;
          border-radius: 4px;
          background: white;
          transition: all 0.2s ease;
          flex-shrink: 0;
          position: relative;
        }

        .court-item-modern:hover .court-checkbox-modern {
          border-color: #E91E63;
        }

        .court-item-modern input[type="checkbox"]:checked ~ .court-checkbox-modern {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-color: transparent;
        }

        .court-item-modern input[type="checkbox"]:checked ~ .court-checkbox-modern::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 3px;
          height: 7px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        .court-name-modern {
          font-size: 13px;
          color: #555;
          font-weight: 500;
        }

        .court-item-modern input[type="checkbox"]:checked ~ .court-name-modern {
          color: #c41849;
          font-weight: 600;
        }

        .no-courts-modern,
        .loading-state-modern,
        .error-state-modern {
          text-align: center;
          padding: 20px;
          font-size: 13px;
          color: #666;
        }

        .loading-state-modern i {
          font-size: 24px;
          color: #E91E63;
          margin-bottom: 8px;
        }

        /* Scrollbar */
        .courts-list-modern::-webkit-scrollbar {
          width: 5px;
        }

        .courts-list-modern::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 4px;
        }

        /* ============================================
           ACTION BUTTONS
        ============================================ */
        .action-buttons-modern {
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 140px;
        }

        .action-btn-modern {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .action-btn-modern i {
          font-size: 18px;
        }

        .search-sc-modern {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .search-sc-modern:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        }

        .search-all-modern {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
        }

        .search-all-modern:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .clear-modern {
          background: #f3f4f6;
          color: #374151;
          border: 2px solid #e5e7eb;
        }

        .clear-modern:hover {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
          transform: translateY(-2px);
        }

        .action-btn-modern:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Reset Date Button */
        .reset-date-modern {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 8px;
        }

        .reset-date-modern:hover:not(:disabled) {
          background: #4f46e5;
          transform: translateY(-1px);
        }

        .reset-date-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 1200px) {
          .search-grid {
            grid-template-columns: 1fr;
          }

          .search-type-cards-mini {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .search-main-content {
            padding-top: 130px;
          }

          .search-hero {
            padding: 30px 15px 25px;
          }

          .hero-title {
            font-size: 1.5rem;
          }

          .hero-subtitle {
            font-size: 0.9rem;
          }

          .search-container {
            margin-top: -20px;
            padding: 0 15px 40px;
          }

          .form-grid-2 {
            grid-template-columns: 1fr;
          }

          .courts-actions-container {
            flex-direction: column;
          }

          .action-buttons-modern {
            width: 100%;
            min-width: 100%;
          }

          .card-body-modern {
            padding: 16px;
          }

          .search-type-cards-mini {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.3rem;
          }

          .hero-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .card-header-modern {
            padding: 12px 16px;
          }

          .header-icon-wrapper {
            width: 32px;
            height: 32px;
            font-size: 16px;
          }

          .card-title-modern {
            font-size: 14px;
          }
        }
               /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Search;